import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getCodeData, getInitSheet } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnSheet from "@shared/components/Grid/SnSheet";
import { globalInfo, menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { IconButton, Tooltip } from "@mui/material";

interface Props {
  Menuprops: menuInfo;
  searchParams: {
    cd_firm: string;
    cd_pjt: string;
    cd_unit: string;
  };
  controllerName: string;
  userInfo: globalInfo["userInfo"];
  modalRef?: any;
}

type EbomRow = Record<string, any>;

const SN_PMS_PJT_REG_004_POP_001 = forwardRef(
  (
    { Menuprops, searchParams, controllerName, userInfo, modalRef }: Props,
    ref
  ) => {
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any>();
    const gridref1 = useRef<any>(null);

    const existingEbomRef = useRef<any[]>([]);
    const masItemCacheRef = useRef<Set<string>>(new Set());
    const comboCacheRef = useRef<
      Map<string, { set: Set<string>; codeMap: Map<string, string> }>
    >(new Map());

    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    const norm = (v: any) =>
      String(v ?? "")
        .trim()
        .toUpperCase();

    const makeCodeMap = (list: Array<{ code: string; name: string }>) => {
      const m = new Map<string, string>();
      (list ?? []).forEach((x) => {
        if (!x) return;
        const code = String(x.code ?? "");
        const name = String(x.name ?? "");
        if (code) m.set(norm(code), code);
        if (name) m.set(norm(name), code);
      });
      return m;
    };

    const toCode = (
      input: any,
      codeMap: Map<string, string>,
      fallback?: string
    ) => {
      const key = norm(input);
      if (!key) return fallback ?? "";
      return codeMap.get(key) ?? fallback ?? String(input ?? "");
    };

    const toNumber = (val: any, fallback = 0) => {
      const num = Number(val);
      return isNaN(num) ? fallback : num;
    };

    useEffect(() => {
      const fetchInit = async () => {
        try {
          const rtn: any = await getInitSheet({});

          rtn.grid.sheet1.push({
            cd_column: "error_msg",
            nm_column: "검증이력",
            qt_width: 240,
            tp_column: "text",
            fg_visible: "Y",
            yn_required: "N",
            dc_editcon: "1 == 1",
            editable: false,
          });

          setColums(rtn.grid.sheet1);
          gridref1.current.resetGridData();
        } catch (error) {}
      };
      fetchInit();
    }, []);

    useEffect(() => {
      const loadExisting = async () => {
        if (
          !searchParams?.cd_firm ||
          !searchParams?.cd_pjt ||
          !searchParams?.cd_unit
        )
          return;

        try {
          const params = {
            id: "sn_pms_pjt_reg_004_01",
            params: {
              cd_firm: searchParams.cd_firm,
              cd_pjt: searchParams.cd_pjt,
              cd_unit: searchParams.cd_unit,
            },
          };
          const data: any = await fnGetDataSet(params);
          existingEbomRef.current = data?.[0] ?? [];
        } catch (error) {
          existingEbomRef.current = [];
        }
      };
      loadExisting();
    }, [searchParams]);

    const onDelete = () => {
      const focusedCell = gridref1.current.api.getFocusedCell();
      const startRowIndex = focusedCell?.rowIndex ?? 0;
      gridref1.current?.deleteRowByIndex(startRowIndex);
    };

    const setErrorMessage = (node: any, msg: string) => {
      const prev = node.data?.error_msg
        ? String(node.data.error_msg) + " | "
        : "";
      gridref1.current.api
        .getRowNode(node.id)
        ?.setDataValue("error_msg", prev + msg);
    };

    const isEmptyRow = (row: EbomRow): boolean => {
      const checkFields = ["no_level", "cd_matl", "cd_item"];
      return checkFields.every((field) => !row[field]);
    };

    const Validation = async (): Promise<boolean> => {
      const api = gridref1.current?.api;
      if (!api) return false;

      const formCdFirm = searchParams?.cd_firm ?? userInfo.cd_firm;
      const formCdPjt = searchParams?.cd_pjt;
      const formCdUnit = searchParams?.cd_unit; // unit 파라미터명에 모품목 값 전달

      if (!formCdPjt || !formCdUnit) {
        await showDialog("alter", "프로젝트/UNIT이 없습니다.");
        return false;
      }

      const nodes: any[] = [];
      api.forEachNode((n: any) => {
        if (n?.data && !isEmptyRow(n.data)) {
          api.getRowNode(n.id)?.setDataValue("error_msg", "");
          nodes.push(n);
        }
      });

      if (nodes.length === 0) {
        await showDialog("alter", "검증할 데이터가 없습니다.");
        return false;
      }

      // mas_item 코드 캐시: 최초 1회 로딩 후 재사용 (cd_item, cd_matl 존재 여부 검증에 사용)
      const masItems: Set<string> =
        masItemCacheRef.current.size > 0
          ? masItemCacheRef.current
          : new Set<string>(
              (await getCodeData("mas_item", "")).map((x: any) =>
                String(x.cd_item ?? "")
              )
            );
      masItemCacheRef.current = masItems;

      // SnComboBox 컬럼 코드 캐시 (code/name 모두 set에 저장)
      const comboCols = (columns || []).filter((c: any) => {
        const tp = String(c?.tp_column || "")
          .trim()
          .toLowerCase();
        return tp === "sncombobox" && c?.cd_column && c?.cd_clas;
      });
      const comboCache = comboCacheRef.current;
      for (const col of comboCols) {
        const clas = String(col.cd_clas || "");
        if (!clas) continue;
        if (!comboCache.has(clas)) {
          const codes = await getCodeData(clas, "");
          const set = new Set<string>();
          const codeMap = makeCodeMap(codes || []);
          codes.forEach((x: any) => {
            if (x?.code) set.add(norm(x.code));
            if (x?.name) set.add(norm(x.name));
          });
          comboCache.set(clas, { set, codeMap });
        }
      }

      const existingData = existingEbomRef.current || [];

      const dateRegex = /^\d{8}$/;
      const isBlank = (v: any) =>
        v === undefined || v === null || String(v).trim() === "";

      const maxNoHstByKey = new Map<string, number>();
      existingData.forEach((r: any) => {
        const key = [r.cd_firm, r.cd_pjt, r.cd_unit, r.cd_item, r.cd_matl].join(
          "|"
        );
        const cur = maxNoHstByKey.get(key) ?? 0;
        const nh = toNumber(r.no_hst, 1);
        maxNoHstByKey.set(key, Math.max(cur, nh));
      });

      let hasError = false;
      const childrenMap = new Map<string, string[]>();
      const nodeById: Record<string, any> = {};
      const parentEdge: Record<string, string> = {};
      const levelStack: Record<number, any> = {};
      const rootLevel = toNumber(nodes[0]?.data?.no_level ?? 0);

      for (let idx = 0; idx < nodes.length; idx++) {
        const node = nodes[idx];
        const row = node.data;
        const curLevel = toNumber(row.no_level, 0);
        const id = String(node.id);
        nodeById[id] = node;

        const derived: EbomRow = {
          ...row,
          cd_firm: row.cd_firm || formCdFirm,
          cd_pjt: row.cd_pjt || formCdPjt,
          cd_unit: formCdUnit, // cd_unit은 전역(최상위 품목)
          cd_item: row.cd_item || undefined,
          no_hst: toNumber(row.no_hst, 1),
        };

        // 첫 행만 루트로 고정 (레벨과 무관하게 cd_item = cd_unit)
        if (idx === 0) {
          derived.cd_item = formCdUnit;
          levelStack[curLevel] = node;
        } else {
          const parentNode = levelStack[curLevel - 1] || null;
          if (!parentNode) {
            setErrorMessage(
              node,
              `레벨 ${curLevel} 부모(레벨 ${curLevel - 1})를 찾을 수 없습니다.`
            );
            hasError = true;
          } else {
            const parentId = String(parentNode.id);
            if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
            childrenMap.get(parentId)!.push(id);
            parentEdge[id] = parentId;
            const parentMatl = parentNode.data?.cd_matl ?? "";
            derived.cd_item = parentMatl;

            const parentCdDummy03 = (parentNode.data?.cd_dummy03 || "").trim();
            if (
              childrenMap.get(parentId)?.length &&
              parentMatl &&
              parentCdDummy03 &&
              !["5", "반제품"].includes(parentCdDummy03)
            ) {
              setErrorMessage(
                parentNode,
                `부모(${parentMatl})는 하위 자식 허용 코드가 아닙니다.`
              );
              hasError = true;
            }
          }
          levelStack[curLevel] = node;
          Object.keys(levelStack)
            .map((k) => Number(k))
            .filter((lvl) => lvl > curLevel)
            .forEach((lvl) => delete levelStack[lvl]);
        }

        const cdItem = derived.cd_item;
        if (!cdItem || !masItems.has(String(cdItem))) {
          setErrorMessage(node, `모품목(${cdItem ?? ""}) 코드가 없습니다.`);
          hasError = true;
        }

        if (!derived.cd_matl || !masItems.has(String(derived.cd_matl))) {
          setErrorMessage(
            node,
            `자품목(${derived.cd_matl ?? ""}) 코드가 없습니다.`
          );
          hasError = true;
        }

        const numericFields = [
          "no_level",
          "no_hst",
          "no_line",
          "qt",
          "um",
          "qt_rcv",
          "qt_prd",
          "qt_isu",
        ];
        numericFields.forEach((key) => {
          const val = derived[key];
          if (!isBlank(val) && isNaN(Number(val))) {
            setErrorMessage(node, `${key} 값이 숫자가 아닙니다.`);
            hasError = true;
          }
        });

        Object.keys(row || {})
          .filter((k) => k.startsWith("dt"))
          .forEach((k) => {
            if (!isBlank(derived[k]) && !dateRegex.test(String(derived[k]))) {
              setErrorMessage(node, `${k} 형식이 올바르지 않습니다(YYYYMMDD).`);
              hasError = true;
            }
          });

        // SnComboBox 값 유효성 체크 (code/name 모두 허용)
        for (const col of comboCols) {
          const val = derived[col.cd_column];
          const clas = String(col.cd_clas || "");
          if (isBlank(val) || !clas || !comboCache.has(clas)) continue;
          const entry = comboCache.get(clas)!;
          if (!entry.set.has(norm(val))) {
            setErrorMessage(
              node,
              `${col.nm_column || col.cd_column}(${val})가 코드(${clas})에 없습니다.`
            );
            hasError = true;
          }
        }
      }

      childrenMap.forEach((kids, pid) => {
        const seen = new Set<string>();
        kids.forEach((cid) => {
          const child = nodeById[cid];
          const key = `${pid}|${child.data.cd_matl ?? ""}`;
          if (seen.has(key)) {
            setErrorMessage(child, "동일 부모-자식이 중복되었습니다.");
            hasError = true;
          }
          seen.add(key);
        });
      });

      const cycleCheck = () => {
        const graph = new Map<string, string[]>();
        Object.keys(parentEdge).forEach((cid) => {
          const pid = parentEdge[cid];
          const parentMatl = nodeById[pid]?.data?.cd_matl ?? "";
          const childMatl = nodeById[cid]?.data?.cd_matl ?? "";
          if (!parentMatl || !childMatl) return;
          if (!graph.has(parentMatl)) graph.set(parentMatl, []);
          graph.get(parentMatl)!.push(childMatl);
        });

        const visiting = new Set<string>();
        const visited = new Set<string>();
        const dfs = (node: string): boolean => {
          if (visiting.has(node)) return true;
          if (visited.has(node)) return false;
          visiting.add(node);
          const next = graph.get(node) || [];
          for (const n of next) {
            if (dfs(n)) return true;
          }
          visiting.delete(node);
          visited.add(node);
          return false;
        };

        for (const key of graph.keys()) {
          if (dfs(key)) return true;
        }
        return false;
      };

      if (cycleCheck()) {
        hasError = true;
        await showDialog("error", "BOM 구조에 순환 참조가 존재합니다.");
      }

      nodes.forEach((node) => {
        const row = node.data;
        const key = [
          row.cd_firm || formCdFirm,
          row.cd_pjt || formCdPjt,
          formCdUnit,
          row.cd_item,
          row.cd_matl,
        ].join("|");
        const maxHst = maxNoHstByKey.get(key) ?? 0;
        const curHst = toNumber(row.no_hst, 1);
        if (curHst < maxHst) {
          setErrorMessage(
            node,
            `no_hst(${curHst})가 기존 최대(${maxHst})보다 작습니다.`
          );
          hasError = true;
        }
      });

      if (hasError) {
        await showDialog(
          "error",
          "검증에 실패했습니다. 오류 메시지를 확인해 주세요."
        );
        return false;
      }
      return true;
    };

    const buildSavePayload = async () => {
      const api = gridref1.current?.api;
      if (!api) return null;

      const formCdPjt = searchParams?.cd_pjt ?? "";
      const formCdUnit = searchParams?.cd_unit ?? "";

      const rows: EbomRow[] = [];
      api.forEachNode((n: any) => {
        if (n?.data && !isEmptyRow(n.data)) rows.push(n.data);
      });

      // SnComboBox 코드맵 (Validation 캐시 사용, 없으면 로드)
      const comboCache = comboCacheRef.current;
      const comboCols = (columns || []).filter((c: any) => {
        const tp = String(c?.tp_column || "")
          .trim()
          .toLowerCase();
        return tp === "sncombobox" && c?.cd_column && c?.cd_clas;
      });
      for (const col of comboCols) {
        const clas = String(col.cd_clas || "");
        if (!clas) continue;
        if (!comboCache.has(clas)) {
          const codes = await getCodeData(clas, "");
          const set = new Set<string>();
          const codeMap = makeCodeMap(codes || []);
          codes.forEach((x: any) => {
            if (x?.code) set.add(norm(x.code));
            if (x?.name) set.add(norm(x.name));
          });
          comboCache.set(clas, { set, codeMap });
        }
      }

      const existingMap = new Map<string, EbomRow>();
      const existingCbByItem = new Map<string, string>();
      let maxCb = 0;
      (existingEbomRef.current || []).forEach((r: any) => {
        const key = [
          r.cd_firm,
          r.cd_pjt,
          r.cd_unit,
          r.cd_item,
          toNumber(r.no_hst, 0),
          r.cd_matl,
        ].join("|");
        existingMap.set(key, r);
        if (r.cd_item && r.cd_cb) {
          existingCbByItem.set(String(r.cd_item), String(r.cd_cb));
          const cbNum = toNumber(r.cd_cb, 0);
          if (cbNum > maxCb) maxCb = cbNum;
        }
      });

      const pkForCompare = [
        "cd_firm",
        "cd_pjt",
        "cd_unit",
        "cd_item",
        "no_hst",
        "cd_matl",
      ];
      const insert: EbomRow[] = [];
      const update: EbomRow[] = [];

      // 1차 패스: 레벨/부모 매핑, 그룹별 no_line max 집계
      const levelStack: Record<number, EbomRow> = {};
      const parentItemMap = new Map<number, string>(); // idx -> parent cd_item
      const derivedRows: { idx: number; derived: EbomRow }[] = [];
      const rootLevel = toNumber(rows[0]?.no_level ?? 0);

      const maxNoLineByGroup = new Map<string, number>();
      existingMap.forEach((v) => {
        const g = [v.cd_firm, v.cd_pjt, v.cd_unit, v.cd_item].join("|");
        const cur = maxNoLineByGroup.get(g) ?? 0;
        const nl = toNumber(v.no_line, 0);
        if (nl > cur) maxNoLineByGroup.set(g, nl);
      });

      rows.forEach((row, idx) => {
        const derived: EbomRow = {
          ...row,
          cd_firm: row.cd_firm || userInfo.cd_firm,
          cd_pjt: row.cd_pjt || formCdPjt,
          cd_unit: formCdUnit,
          cd_item: row.cd_item || undefined,
          no_hst: toNumber(row.no_hst, 1),
          no_line: toNumber(row.no_line, 0),
        };

        const curLevel = toNumber(row.no_level, 0);
        if (idx === 0) {
          derived.cd_item = formCdUnit;
          levelStack[curLevel] = { ...derived };
          parentItemMap.set(idx, "");
        } else {
          const parent = levelStack[curLevel - 1];
          if (parent) {
            derived.cd_item = parent.cd_matl || derived.cd_item;
            parentItemMap.set(idx, parent.cd_matl || "");
          } else {
            parentItemMap.set(idx, "");
          }
          levelStack[curLevel] = { ...derived };
          Object.keys(levelStack)
            .map((k) => Number(k))
            .filter((lvl) => lvl > curLevel)
            .forEach((lvl) => delete levelStack[lvl]);
        }

        const groupKey = [
          derived.cd_firm,
          derived.cd_pjt,
          derived.cd_unit,
          derived.cd_item,
        ].join("|");
        const curMax = maxNoLineByGroup.get(groupKey) ?? 0;
        if (derived.no_line > curMax) {
          maxNoLineByGroup.set(groupKey, derived.no_line);
        }

        derivedRows.push({ idx, derived });
      });

      // 자식 개수 집계 (cd_item 기준)
      const childrenCount = new Map<string, number>();
      derivedRows.forEach(({ idx, derived }) => {
        if (idx === 0) return;
        const parentItem = parentItemMap.get(idx) || "";
        if (!parentItem) return;
        childrenCount.set(parentItem, (childrenCount.get(parentItem) ?? 0) + 1);
      });

      // cb 채번: 자식이 있는 모품목에만 신규 채번
      const cbByItem = new Map<string, string>();
      existingCbByItem.forEach((v, k) => cbByItem.set(k, v));
      derivedRows.forEach(({ idx, derived }) => {
        if (idx === 0) return;
        const item = String(derived.cd_matl || "");
        const hasChildren = (childrenCount.get(item) ?? 0) > 0;
        if (!hasChildren) return;
        if (cbByItem.has(item)) return;
        maxCb += 1;
        cbByItem.set(item, String(maxCb));
      });

      // 2차 패스: no_line 채번, 콤보 코드 치환, insert/update 결정
      derivedRows.forEach(({ idx, derived }) => {
        if (idx === 0) return; // 첫 행 저장 제외

        // no_line 자동 채번
        const groupKey = [
          derived.cd_firm,
          derived.cd_pjt,
          derived.cd_unit,
          derived.cd_item,
        ].join("|");
        if (!derived.no_line) {
          const curMax = maxNoLineByGroup.get(groupKey) ?? 0;
          const next = curMax + 1;
          derived.no_line = next;
          maxNoLineByGroup.set(groupKey, next);
        }

        // cd_cb/no_cbs: 자식 있는 모품목이면 자기 cb, 없으면 부모 cb 상속
        const selfItem = String(derived.cd_matl || "");
        const parentItem = parentItemMap.get(idx) || "";
        let cbVal = cbByItem.get(selfItem);
        if (!cbVal && parentItem) {
          cbVal = cbByItem.get(parentItem);
        }
        if (cbVal) {
          derived.cd_cb = cbVal;
          derived.no_cbs = `${formCdPjt}${cbVal}`;
        }

        // SnComboBox 코드 치환
        for (const col of comboCols) {
          const clas = String(col.cd_clas || "");
          if (!clas || !comboCache.has(clas)) continue;
          const codeMap = comboCache.get(clas)!.codeMap;
          const val = derived[col.cd_column];
          const converted = codeMap.get(norm(val));
          if (converted) {
            derived[col.cd_column] = converted;
          }
        }

        const key = [
          derived.cd_firm,
          derived.cd_pjt,
          derived.cd_unit,
          derived.cd_item,
          derived.no_hst,
          derived.cd_matl,
        ].join("|");

        const existing = existingMap.get(key);
        if (!existing) {
          insert.push({ ...derived, fg_iud: "I" });
          return;
        }

        const compareColumns = columns
          .map((c: any) => c.cd_column)
          .filter((c: string) => !!c && !pkForCompare.includes(c));

        const hasDiff = compareColumns.some((col) => {
          const prevVal = existing[col];
          const newVal = derived[col];
          return String(prevVal ?? "") !== String(newVal ?? "");
        });

        if (hasDiff) {
          update.push({
            ...existing,
            ...derived,
            no_line: existing.no_line ?? derived.no_line,
            fg_iud: "U",
          });
        }
      });

      return {
        pms_pjt_ebom: {
          insert,
          update,
          modifiedRowcount: insert.length + update.length,
        },
      };
    };

    const onSave = async () => {
      try {
        const api = gridref1.current?.api;
        if (!api) return;

        const isValid = await Validation();
        if (!isValid) return;

        const payload = await buildSavePayload();
        if (!payload) return;

        if (payload.pms_pjt_ebom.modifiedRowcount === 0) {
          await showDialog("alter", "변경된 데이터가 없습니다.");
          return;
        }
        await fnSave(controllerName, payload);
        gridref1.current.resetUpdate();
        gridref1.current.resetGridData();
        // 저장 성공 시 팝업 닫기 + 부모 재조회 트리거
        if (modalRef?.current?.handleConfirm) {
          modalRef.current.handleConfirm(true, { excelUpload: true });
        }
      } catch (error: any) {}
    };

    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };

    useImperativeHandle(ref, () => ({
      onDelete,
      onSave,
    }));

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_Init") {
        gridref1.current.resetGridData();
      }

      if (id == "aButton_Validation") {
        const isValid = await Validation();
        if (isValid) {
          await showDialog("alter", "모든 데이터 검증이 완료되었습니다.");
        }
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={grid1Width}
            minWidth={130}
            onResizeStop={onResizeStopH}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: grid1Width }}
          >
            <SnSheet
              ref={gridref1}
              enableAddRow={true}
              columnConfig={columns}
              rowData={dataSource1}
              isEditable={true}
              title={Menuprops.nm_menu}
              header={
                <div>
                  <SnButton
                    text={"초기화"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Init");
                    }}
                  />
                  <SnButton
                    text={"검증체크"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Validation");
                    }}
                  />
                  <Tooltip title="Save">
                    <IconButton
                      onClick={onSave}
                      sx={{
                        color: "gray",
                        "&:hover": {
                          color: "blue",
                          transform: "scale(1.2)",
                          transition: "all 0.3s ease",
                        },
                      }}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={onDelete}
                      sx={{
                        color: "gray",
                        "&:hover": {
                          color: "blue",
                          transform: "scale(1.2)",
                          transition: "all 0.3s ease",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              }
              codeDataList={[]}
              compareFields={[]}
            />
          </SnResizableLNR>
        </div>
      </div>
    );
  }
);

export default SN_PMS_PJT_REG_004_POP_001;
